/**
 * FacilityLensTroubleshoot -- Interactive symptom-based troubleshooting flow.
 *
 * Flow:
 *  1. Symptom Cards grid -- user picks what they're experiencing
 *  2. Follow-up Questions -- 2-3 quick multiple-choice to narrow down
 *  3. Diagnosis Panel -- likely cause with self-fix steps or escalation
 */

import { useEffect, useMemo } from "react";
import { Button, Tag } from "antd";
import {
	ThunderboltOutlined,
	SoundOutlined,
	FireOutlined,
	ExperimentOutlined,
	EyeOutlined,
	DashboardOutlined,
	AlertOutlined,
	QuestionCircleOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	ToolOutlined,
	ArrowLeftOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { useFacilityLensStore } from "@/stores/facility-lens-store";
import type { SymptomCard, FollowUpQuestion, LikelyCause } from "@/services/facility-lens-service";
import "./FacilityLensTroubleshoot.scss";

// ─── Icon mapping for symptom categories ─────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
	temperature: <FireOutlined />,
	noise: <SoundOutlined />,
	power: <ThunderboltOutlined />,
	leak: <ExperimentOutlined />,
	visual: <EyeOutlined />,
	performance: <DashboardOutlined />,
	odor: <AlertOutlined />,
	other: <QuestionCircleOutlined />,
};

// ─── Decision Tree Helpers ────────────────────────────────────────────────────

/**
 * Returns only the follow-up questions whose `showWhen` condition is met
 * (or that have no `showWhen`). Each entry carries its *original* index so we
 * can map answers back to the correct `indicators` slot.
 */
function getVisibleQuestions(
	questions: FollowUpQuestion[],
	answers: Record<number, string>,
): { question: FollowUpQuestion; originalIndex: number }[] {
	return questions
		.map((q, i) => ({ question: q, originalIndex: i }))
		.filter(({ question }) => {
			if (!question.showWhen) return true;
			const { questionIndex, answer } = question.showWhen;
			return answers[questionIndex] === answer;
		});
}

/**
 * Score each likely cause based on how many of its `indicators` match the
 * user's answers. Returns the best cause, preferring self-fixable on ties.
 * Falls back to `likelyCauses[0]` when no indicators exist (old cached data).
 */
function scoreCauses(
	causes: LikelyCause[],
	answers: Record<number, string>,
): LikelyCause | null {
	if (!causes.length) return null;

	// If *no* cause has indicators, fall back to old behavior (first cause)
	const anyHasIndicators = causes.some((c) => c.indicators && c.indicators.length > 0);
	if (!anyHasIndicators) return causes[0];

	let bestCause = causes[0];
	let bestScore = -1;

	for (const cause of causes) {
		let score = 0;
		if (cause.indicators) {
			for (const ind of cause.indicators) {
				const userAnswer = answers[ind.questionIndex];
				if (userAnswer && ind.answers.includes(userAnswer)) {
					score += 1;
				}
			}
		}
		// Higher score wins; on tie, prefer self-fixable (try easy fix first)
		if (
			score > bestScore ||
			(score === bestScore && cause.selfFixable && !bestCause.selfFixable)
		) {
			bestScore = score;
			bestCause = cause;
		}
	}

	return bestCause;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
	symptomCards: SymptomCard[];
	onCreateWorkOrder: (context: string) => void;
}

const FacilityLensTroubleshoot = ({ symptomCards, onCreateWorkOrder }: Props) => {
	const {
		troubleshoot,
		selectSymptom,
		answerFollowUp,
		setDiagnosis,
		setSelfFixAttempted,
		setSelfFixResolved,
		resetTroubleshoot,
	} = useFacilityLensStore();

	const {
		selectedSymptomIndex,
		followUpAnswers,
		diagnosis,
		selfFixAttempted,
		selfFixResolved,
	} = troubleshoot;

	const activeCard = selectedSymptomIndex !== null ? symptomCards[selectedSymptomIndex] : null;

	// Compute visible questions (respecting showWhen conditions)
	const visibleQuestions = useMemo(
		() => (activeCard ? getVisibleQuestions(activeCard.followUpQuestions, followUpAnswers) : []),
		[activeCard, followUpAnswers],
	);

	// Check if all *visible* follow-up questions have been answered
	const allQuestionsAnswered = visibleQuestions.length > 0
		? visibleQuestions.every(({ originalIndex }) => followUpAnswers[originalIndex] !== undefined)
		: false;

	// Score causes using the decision tree indicators
	const suggestedCause: LikelyCause | null = useMemo(() => {
		if (diagnosis) return diagnosis;
		if (!activeCard || !allQuestionsAnswered) return null;
		return scoreCauses(activeCard.likelyCauses, followUpAnswers);
	}, [activeCard, allQuestionsAnswered, diagnosis, followUpAnswers]);

	// Auto-set diagnosis once all questions are answered
	useEffect(() => {
		if (activeCard && allQuestionsAnswered && !diagnosis && suggestedCause) {
			setDiagnosis(suggestedCause);
		}
	}, [activeCard, allQuestionsAnswered, diagnosis, suggestedCause]);

	// Build context string for work order enrichment
	const buildWoContext = (): string => {
		if (!activeCard) return "";
		const lines: string[] = [];
		lines.push(`**Reported Symptom:** ${activeCard.symptom}`);
		activeCard.followUpQuestions.forEach((q, i) => {
			const answer = followUpAnswers[i];
			if (answer) lines.push(`- ${q.question} -> ${answer}`);
		});
		if (diagnosis) {
			lines.push(`**AI Diagnosis:** ${diagnosis.cause}`);
			if (selfFixAttempted && diagnosis.selfFixable) {
				lines.push(`**Self-fix attempted:** ${diagnosis.fixSteps}`);
				lines.push(`**Resolved:** ${selfFixResolved === true ? "Yes" : "No"}`);
			}
		}
		return lines.join("\n");
	};

	// ─── Step 1: Symptom Grid ───
	if (selectedSymptomIndex === null) {
		return (
			<div className="flt-container">
				<div className="flt-header">
					<h3>What are you experiencing?</h3>
					<p>Select the symptom that best describes the issue.</p>
				</div>
				<div className="flt-symptom-grid">
					{symptomCards.map((card, idx) => (
						<button
							key={idx}
							className="flt-symptom-card"
							onClick={() => selectSymptom(idx)}
						>
							<div className="flt-symptom-icon">
								{ICON_MAP[card.icon] || ICON_MAP.other}
							</div>
							<span className="flt-symptom-label">{card.symptom}</span>
						</button>
					))}
				</div>
			</div>
		);
	}

	// ─── Step 2: Follow-up Questions ───
	if (!allQuestionsAnswered && activeCard) {
		// Find the first unanswered *visible* question
		const unansweredVisible = visibleQuestions.find(
			({ originalIndex }) => followUpAnswers[originalIndex] === undefined,
		);
		if (!unansweredVisible) {
			// Edge case: all visible questions answered but allQuestionsAnswered was false
			// (shouldn't happen, but safety net)
			return null;
		}
		const currentQ = unansweredVisible.question;
		const currentQIdx = unansweredVisible.originalIndex;

		// Progress counts only visible questions
		const answeredVisibleCount = visibleQuestions.filter(
			({ originalIndex }) => followUpAnswers[originalIndex] !== undefined,
		).length;
		const total = visibleQuestions.length;

		return (
			<div className="flt-container">
				<button className="flt-back-btn" onClick={resetTroubleshoot}>
					<ArrowLeftOutlined /> Back to symptoms
				</button>

				<div className="flt-selected-symptom">
					<div className="flt-symptom-icon-sm">
						{ICON_MAP[activeCard.icon] || ICON_MAP.other}
					</div>
					<span>{activeCard.symptom}</span>
				</div>

				<div className="flt-followup">
					<div className="flt-progress">
						<div className="flt-progress-bar" style={{ width: `${(answeredVisibleCount / total) * 100}%` }} />
					</div>
					<p className="flt-question-label">
						Question {answeredVisibleCount + 1} of {total}
					</p>
					<h3 className="flt-question">{currentQ.question}</h3>
					<div className="flt-options">
						{currentQ.options.map((opt, oi) => (
							<button
								key={oi}
								className="flt-option-btn"
								onClick={() => answerFollowUp(currentQIdx, opt)}
							>
								{opt}
							</button>
						))}
					</div>
				</div>
			</div>
		);
	}

	// ─── Step 3: Diagnosis ───
	if (activeCard) {
		const cause = suggestedCause;

		// Self-fix: show steps and ask if it resolved
		if (cause?.selfFixable && !selfFixAttempted) {
			return (
				<div className="flt-container">
					<button className="flt-back-btn" onClick={resetTroubleshoot}>
						<ArrowLeftOutlined /> Start over
					</button>

					<div className="flt-diagnosis">
						<div className="flt-diagnosis-header flt-self-fix">
							<CheckCircleOutlined className="flt-diag-icon" />
							<div>
								<h3>This might be fixable</h3>
								<p className="flt-cause-name">{cause.cause}</p>
							</div>
						</div>

						<div className="flt-fix-steps">
							<h4>Try this:</h4>
							<p>{cause.fixSteps}</p>
						</div>

						<div className="flt-fix-confirm">
							<p>Did this solve the problem?</p>
							<div className="flt-fix-btns">
								<Button
									type="primary"
									icon={<CheckCircleOutlined />}
									onClick={() => {
										setSelfFixAttempted(true);
										setSelfFixResolved(true);
									}}
									className="flt-yes-btn"
								>
									Yes, it's fixed!
								</Button>
								<Button
									icon={<CloseCircleOutlined />}
									onClick={() => {
										setSelfFixAttempted(true);
										setSelfFixResolved(false);
									}}
									className="flt-no-btn"
								>
									No, still broken
								</Button>
							</div>
						</div>
					</div>
				</div>
			);
		}

		// Self-fix resolved
		if (selfFixResolved === true) {
			return (
				<div className="flt-container">
					<div className="flt-resolved">
						<CheckCircleOutlined className="flt-resolved-icon" />
						<h3>Great, glad that worked!</h3>
						<p>If the problem comes back, you can scan the asset again.</p>
						<Button type="text" onClick={resetTroubleshoot}>
							Try another symptom
						</Button>
					</div>
				</div>
			);
		}

		// Needs a technician (either not self-fixable, or self-fix didn't work)
		const escalationCause = !cause?.selfFixable
			? cause
			: activeCard.likelyCauses.find((c) => !c.selfFixable) ?? cause;

		return (
			<div className="flt-container">
				<button className="flt-back-btn" onClick={resetTroubleshoot}>
					<ArrowLeftOutlined /> Start over
				</button>

				<div className="flt-diagnosis">
					<div className="flt-diagnosis-header flt-needs-tech">
						<ToolOutlined className="flt-diag-icon" />
						<div>
							<h3>This needs a technician</h3>
							<p className="flt-cause-name">{escalationCause?.cause ?? "Professional assessment required"}</p>
							{escalationCause?.severity && (
								<Tag
									color={
										escalationCause.severity === "CRITICAL" ? "red" :
										escalationCause.severity === "HIGH" ? "orange" :
										escalationCause.severity === "MEDIUM" ? "gold" : "blue"
									}
								>
									{escalationCause.severity} Priority
								</Tag>
							)}
						</div>
					</div>

					{selfFixAttempted && selfFixResolved === false && (
						<div className="flt-attempted-note">
							<p>
								You tried: <em>{cause?.fixSteps}</em> -- but the problem persists.
								This information will be included in the work order.
							</p>
						</div>
					)}

					<Button
						type="primary"
						size="large"
						icon={<PlusOutlined />}
						block
						onClick={() => onCreateWorkOrder(buildWoContext())}
						className="flt-escalate-btn"
					>
						Create Work Order
					</Button>

					<Button
						type="text"
						onClick={resetTroubleshoot}
						style={{ width: "100%", marginTop: 8 }}
					>
						Try a different symptom
					</Button>
				</div>
			</div>
		);
	}

	return null;
};

export default FacilityLensTroubleshoot;
